import networksConfig from '../data/networks.json';
import type { Network, DetectionResult, AddressFormat } from '../types/blockchain.types';

export class NetworkDetector {
  private networks: Network[];

  constructor() {
    this.networks = networksConfig.networks as Network[];
  }

  public detectAddress(address: string): DetectionResult {
    const trimmedAddress = address.trim();

    const compatibleNetworks = this.networks.filter((network) => {
      const regex = new RegExp(network.address_regex);
      return regex.test(trimmedAddress);
    });

    if (compatibleNetworks.length === 0) {
      return {
        address: trimmedAddress,
        detected_format: 'evm',
        compatible_networks: [],
        suggested_network: undefined,
      };
    }

    const detectedFormat = this.getAddressFormat(trimmedAddress);

    const suggestedNetwork = this.suggestNetwork(compatibleNetworks, trimmedAddress);

    return {
      address: trimmedAddress,
      detected_format: detectedFormat,
      compatible_networks: compatibleNetworks,
      suggested_network: suggestedNetwork,
    };
  }

  private getAddressFormat(address: string): AddressFormat {
    if (address.startsWith('0x')) {
      return 'evm';
    }

    if (address.match(/^(1|3|bc1)[a-zA-HJ-NP-Z0-9]{25,62}$/)) {
      return 'bitcoin';
    }

    if (address.match(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/)) {
      return 'base58';
    }

    return 'evm';
  }

  private suggestNetwork(compatibleNetworks: Network[], address: string): Network | undefined {
    if (compatibleNetworks.length === 1) {
      return compatibleNetworks[0];
    }

    const format = this.getAddressFormat(address);

    if (format === 'bitcoin') {
      return compatibleNetworks.find((n) => n.id === 'bitcoin');
    }

    if (format === 'base58') {
      return compatibleNetworks.find((n) => n.id === 'solana');
    }

    if (format === 'evm') {
      const priorityOrder: string[] = ['ethereum', 'bsc', 'polygon', 'arbitrum'];

      for (const networkId of priorityOrder) {
        const network = compatibleNetworks.find((n) => n.id === networkId);
        if (network) {
          return network;
        }
      }

      return compatibleNetworks[0];
    }

    return compatibleNetworks[0];
  }

  public getAllNetworks(): Network[] {
    return this.networks;
  }

  public getNetworkById(id: string): Network | undefined {
    return this.networks.find((n) => n.id === id);
  }

  public getEVMNetworks(): Network[] {
    return this.networks.filter((n) => n.address_format === 'evm');
  }

  public validateAddress(address: string, networkId: string): boolean {
    const network = this.getNetworkById(networkId);
    if (!network) {
      return false;
    }

    const regex = new RegExp(network.address_regex);
    return regex.test(address.trim());
  }
}

export const networkDetector = new NetworkDetector();
